/**
 * Collaboration Workspace help bot — situation detection + English chips.
 * Shared pure logic (web imports via @shared or packages path; server via relative @shared).
 */

export const COLLABORATION_HELP_SITUATIONS = [
  "preview_anonymous",
  "requester_waiting",
  "requester_needs_pay",
  "receiver_review_request",
  "receiver_needs_pay",
  "both_paid_active",
  "declined",
  "request_expired",
  "payment_expired",
  "read_only_window",
] as const;

export type CollaborationHelpSituationId = (typeof COLLABORATION_HELP_SITUATIONS)[number];

export type CollaborationHelpRole = "requester" | "receiver" | "preview" | "unknown";

/** Match-shaped fields used to resolve situation (no PII). */
export type CollaborationHelpMatchInput = {
  status: string | null | undefined;
  requesterCompanyId?: string | null;
  receiverCompanyId?: string | null;
  requesterPaid?: boolean | null;
  receiverPaid?: boolean | null;
};

export type ResolveCollaborationHelpInput = {
  pathname: string;
  match: CollaborationHelpMatchInput | null;
  userCompanyId: string | null | undefined;
  collaborationReadOnly?: boolean;
};

export type CollaborationHelpChip = {
  /** Stable i18n key suffix: collabHelp.chips.<situation>.<id> */
  id: string;
  i18nKey: string;
  /** English v1 label (also used as free-form question text when chip tapped). */
  labelEn: string;
};

export type CollaborationHelpContext = {
  situationId: CollaborationHelpSituationId;
  role: CollaborationHelpRole;
  status: string | null;
  workspaceLocked: boolean;
  collaborationReadOnly: boolean;
  userPaid: boolean;
  pathname: string;
  chips: CollaborationHelpChip[];
};

const STATUS = {
  REQUESTED: "REQUESTED",
  RECEIVER_DECLINED: "RECEIVER_DECLINED",
  REQUEST_EXPIRED: "REQUEST_EXPIRED",
  BOTH_ACCEPTED: "BOTH_ACCEPTED",
  REQUESTER_PAID_ONLY: "REQUESTER_PAID_ONLY",
  RECEIVER_PAID_ONLY: "RECEIVER_PAID_ONLY",
  PAYMENT_EXPIRED: "PAYMENT_EXPIRED",
  BOTH_PAID: "BOTH_PAID",
} as const;

function chip(
  situationId: CollaborationHelpSituationId,
  id: string,
  labelEn: string,
): CollaborationHelpChip {
  return {
    id,
    i18nKey: `collabHelp.chips.${situationId}.${id}`,
    labelEn,
  };
}

export const COLLABORATION_HELP_CHIPS_EN: Record<
  CollaborationHelpSituationId,
  CollaborationHelpChip[]
> = {
  preview_anonymous: [
    chip("preview_anonymous", "what_gtid", "What is GTID / why XXX?"),
    chip("preview_anonymous", "how_send", "How do I send a request?"),
    chip("preview_anonymous", "what_explore", "What is Explore Opportunities?"),
    chip("preview_anonymous", "when_unlock", "When do I see their real name?"),
  ],
  requester_waiting: [
    chip("requester_waiting", "what_next", "What happens next?"),
    chip("requester_waiting", "when_name", "When do I see their name?"),
    chip("requester_waiting", "can_pay", "Do I need to pay now?"),
  ],
  requester_needs_pay: [
    chip("requester_needs_pay", "how_pay", "How do I pay?"),
    chip("requester_needs_pay", "credits", "How do collaborations work?"),
    chip("requester_needs_pay", "nda_again", "Is NDA required again?"),
  ],
  receiver_review_request: [
    chip("receiver_review_request", "how_accept", "How do I accept the request?"),
    chip("receiver_review_request", "how_decline", "How do I decline?"),
    chip("receiver_review_request", "what_pitch", "What is the collaboration pitch?"),
  ],
  receiver_needs_pay: [
    chip("receiver_needs_pay", "how_pay_unlock", "How do I pay and unlock?"),
    chip("receiver_needs_pay", "nda_here", "What is the NDA at payment?"),
    chip("receiver_needs_pay", "after_pay", "What unlocks after payment?"),
  ],
  both_paid_active: [
    chip("both_paid_active", "how_chat", "How do I chat with this company?"),
    chip("both_paid_active", "partner_readiness", "What is the Reports tab?"),
    chip("both_paid_active", "shared_docs", "How do shared documents work?"),
    chip("both_paid_active", "meetings", "How do I schedule a meeting?"),
  ],
  declined: [
    chip("declined", "try_again", "Can I try again?"),
    chip("declined", "what_decline", "What does decline mean?"),
  ],
  request_expired: [
    chip("request_expired", "why_expired", "Why did this request expire?"),
    chip("request_expired", "try_again", "Can I send a new request?"),
  ],
  payment_expired: [
    chip("payment_expired", "credits_back", "What happened to my collaborations?"),
    chip("payment_expired", "reconnect", "Can we reconnect?"),
  ],
  read_only_window: [
    chip("read_only_window", "why_no_msg", "Why can’t I send messages?"),
    chip("read_only_window", "still_read", "Can I still read documents?"),
  ],
};

/** Canned offline replies when LLM unavailable. */
export const COLLABORATION_HELP_CANNED_EN: Record<CollaborationHelpSituationId, string> = {
  preview_anonymous:
    "This is an anonymized company profile. Contacts show as XXX for privacy. To start a collaboration, use Explore Opportunities on this page. After both companies pay the engagement fee, the full name and contacts unlock.",
  requester_waiting:
    "Your collaboration request was sent. The other company reviews your pitch and can unlock (pay) or decline. If they do not accept within 28 days, the request expires. Keep contacts masked until both sides complete payment.",
  requester_needs_pay:
    "Complete your engagement payment from the yellow payment controls on this workspace. If you already accepted the NDA at Explore Opportunities, you may not see it again. The workspace fully unlocks only after both companies pay.",
  receiver_review_request:
    "A company sent you an anonymized collaboration pitch. Review the synopsis, pick one role card, answer 10 match questions, then pay (NDA at payment) or Decline with a reason. This request expires in 28 days if you do not respond. After you pay, both sides can download the shared report. Full identity opens after both sides have paid.",
  receiver_needs_pay:
    "Finish engagement payment and accept the Mutual NDA in the payment step. After you pay, both companies can download the shared report. Chat, documents, and meetings unlock when both companies have paid.",
  both_paid_active:
    "Both companies paid — the workspace is unlocked. Use the Reports tab for the persisted collaboration report, Shared Documents for files and chat, and Meetings to schedule. Overview shows the partner’s full identity.",
  declined:
    "This collaboration request was declined. You can use Explore Opportunities again later if the platform allows a new request to this company.",
  request_expired:
    "This collaboration request expired after 28 days without accept or decline. You can use Explore Opportunities again to send a new request to this company.",
  payment_expired:
    "The payment window for this collaboration expired. Incomplete engagement payments may restore collaborations per GTsetu rules. You may need a new collaboration request to try again.",
  read_only_window:
    "This collaboration is view-only. The active collaboration period ends 45 days after both parties paid. You can read history, but editing, new messages, and new uploads are closed.",
};

export function isCollaborationWorkspacePath(pathname: string): boolean {
  const p = (pathname || "").split("?")[0] || "";
  if (p === "/collaborations/company" || p.startsWith("/collaborations/company/")) return true;
  // Match workspace: /collaborations/:id but not list /collaborations
  if (/^\/collaborations\/[^/]+\/?$/.test(p) && !p.startsWith("/collaborations/company")) {
    return true;
  }
  // legacy profile under match
  if (/^\/collaborations\/[^/]+\/profile\/?$/.test(p)) return true;
  return false;
}

export function isCollaborationPreviewPath(pathname: string): boolean {
  const p = (pathname || "").split("?")[0] || "";
  return p === "/collaborations/company" || p.startsWith("/collaborations/company/");
}

function isPaymentPhaseStatus(status: string): boolean {
  return (
    status === STATUS.BOTH_ACCEPTED ||
    status === STATUS.REQUESTER_PAID_ONLY ||
    status === STATUS.RECEIVER_PAID_ONLY
  );
}

/**
 * Resolve workspace help situation for chips + LLM state injection.
 */
export function resolveCollaborationHelpContext(
  input: ResolveCollaborationHelpInput,
): CollaborationHelpContext {
  const pathname = input.pathname || "";
  const match = input.match;
  const userCompanyId = input.userCompanyId?.trim() || null;
  const status = match?.status?.trim() || null;
  const collaborationReadOnly = Boolean(input.collaborationReadOnly);
  const workspaceLocked = status !== STATUS.BOTH_PAID;

  let role: CollaborationHelpRole = "unknown";
  if (!match || !status) {
    role = isCollaborationPreviewPath(pathname) ? "preview" : "unknown";
  } else if (userCompanyId && match.requesterCompanyId === userCompanyId) {
    role = "requester";
  } else if (userCompanyId && match.receiverCompanyId === userCompanyId) {
    role = "receiver";
  }

  const userPaid =
    role === "requester"
      ? Boolean(match?.requesterPaid)
      : role === "receiver"
        ? Boolean(match?.receiverPaid)
        : false;

  let situationId: CollaborationHelpSituationId = "preview_anonymous";

  if (status === STATUS.RECEIVER_DECLINED) {
    situationId = "declined";
  } else if (status === STATUS.REQUEST_EXPIRED) {
    situationId = "request_expired";
  } else if (status === STATUS.PAYMENT_EXPIRED) {
    situationId = "payment_expired";
  } else if (status === STATUS.BOTH_PAID && collaborationReadOnly) {
    situationId = "read_only_window";
  } else if (status === STATUS.BOTH_PAID) {
    situationId = "both_paid_active";
  } else if (role === "receiver" && status === STATUS.REQUESTED) {
    situationId = "receiver_review_request";
  } else if (role === "requester" && status === STATUS.REQUESTED) {
    situationId = "requester_waiting";
  } else if (role === "receiver" && status && isPaymentPhaseStatus(status) && !userPaid) {
    situationId = "receiver_needs_pay";
  } else if (role === "requester" && status && isPaymentPhaseStatus(status) && !userPaid) {
    situationId = "requester_needs_pay";
  } else if (role === "requester" && status && isPaymentPhaseStatus(status) && userPaid) {
    // Waiting on other party payment
    situationId = "requester_waiting";
  } else if (role === "receiver" && status && isPaymentPhaseStatus(status) && userPaid) {
    situationId = "receiver_needs_pay"; // still waiting other side — guide about unlock remaining
    // After receiver paid only, better messaging is still payment phase for partner;
    // use requester_waiting chips feel wrong. Use a waiting-oriented chip set from receiver:
    // Keep receiver_needs_pay only when !userPaid; when userPaid show after_pay info.
  } else if (!match || !status || role === "preview" || role === "unknown") {
    situationId = "preview_anonymous";
  } else {
    situationId = "preview_anonymous";
  }

  // Refine: receiver paid, payment phase, waiting for other → still "receiver_needs_pay" playbook
  // is wrong (they already paid). Prefer waiting-themed guidance via requester_waiting canned
  // only for requester. For receiver who paid: use after_pay chips under a soft waiting.
  // Use both_paid only when BOTH_PAID. When user paid half: map to waiting-style:
  if (status && isPaymentPhaseStatus(status) && userPaid && role === "receiver") {
    situationId = "requester_waiting"; // "what happens next / when name" appropriate while waiting
  }

  return {
    situationId,
    role,
    status,
    workspaceLocked,
    collaborationReadOnly,
    userPaid,
    pathname,
    chips: COLLABORATION_HELP_CHIPS_EN[situationId],
  };
}
